import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initMercadoPago } from '@mercadopago/sdk-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from "./features/auth/context";
import { PathHistoryProvider } from "./shared/hooks";
import { LoadingProvider, Loading } from "./features/loading";
import { ScrollToTop } from "./shared/components/scroll-to-top";
import Routes from "./routes";
import "./index.css";

// Initialize MercadoPago
initMercadoPago(import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || '',);

// Component to trigger viewport adjustment on mobile
function ViewportAdjuster() {
  useEffect(() => {
    // Only trigger on mobile devices
    if (window.innerWidth <= 799) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Simulate a touch event to trigger viewport adjustment
        const touchEvent = new TouchEvent('touchstart', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.body.dispatchEvent(touchEvent);
      }, 100);
    }
  }, []);

  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: 3,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PathHistoryProvider>
          <LoadingProvider>
            <AuthProvider>
              <ViewportAdjuster />
              <ScrollToTop />
              <Loading />
              <Routes />
              <ToastContainer
                position="top-left"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
            </AuthProvider>
          </LoadingProvider>
        </PathHistoryProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);